import { Alert, Checkbox, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { Card } from '../shared/Card'
import { StatusChip } from '../shared/StatusChip'
import { extractApiMessage } from '../../lib/apiError'
import type { AttendeeDeliveryMode } from '../../types/lobbyDay'
import type { EmailSourceKind, RecipientPreviewResponse } from '../../types/communications'

const DELIVERY_MODE_OPTIONS: { value: AttendeeDeliveryMode; label: string }[] = [
  { value: 'App', label: 'App' },
  { value: 'Paper', label: 'Paper' },
  { value: 'Both', label: 'Both' },
]

function sourceKindLabel(kind: EmailSourceKind): string {
  return kind === 'LobbyDayEvent' ? 'Lobby Day event' : 'Contact list'
}

interface RecipientPreviewCardProps {
  sourceKind: EmailSourceKind
  deliveryModes: AttendeeDeliveryMode[]
  onDeliveryModesChange: (modes: AttendeeDeliveryMode[]) => void
  preview: RecipientPreviewResponse | undefined
  isLoading: boolean
  error: unknown
}

/** Source name + (Lobby Day only) DeliveryMode filter checkboxes + live recipient counts (spec
 * §8). Leaving every checkbox unchecked is deliberate, not "select none" -- the api layer
 * normalizes an empty/omitted deliveryModes to "all modes" (task-9 fix round 1), so this
 * component doesn't need its own empty-selection special case. */
export function RecipientPreviewCard({
  sourceKind, deliveryModes, onDeliveryModesChange, preview, isLoading, error,
}: RecipientPreviewCardProps) {
  const toggleMode = (mode: AttendeeDeliveryMode, checked: boolean) => {
    if (checked) onDeliveryModesChange([...deliveryModes, mode])
    else onDeliveryModesChange(deliveryModes.filter((m) => m !== mode))
  }

  return (
    <Card>
      <Text fontWeight="semibold" mb={1}>Recipients</Text>
      <Text fontSize="sm" color="fg.muted" mb={3}>{sourceKindLabel(sourceKind)}</Text>

      {sourceKind === 'LobbyDayEvent' && (
        <VStack align="stretch" gap={1} mb={4}>
          <Text fontSize="xs" color="fg.muted">Delivery mode (leave all unchecked to include every mode)</Text>
          <HStack gap={4} wrap="wrap">
            {DELIVERY_MODE_OPTIONS.map((opt) => (
              <Checkbox.Root
                key={opt.value}
                size="sm"
                checked={deliveryModes.includes(opt.value)}
                onCheckedChange={(e) => toggleMode(opt.value, !!e.checked)}
                _touch={{ minH: '2.75rem' }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                <Checkbox.Label>{opt.label}</Checkbox.Label>
              </Checkbox.Root>
            ))}
          </HStack>
        </VStack>
      )}

      {isLoading && <Spinner size="sm" color="accent" />}

      {!!error && (
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>{extractApiMessage(error, 'Could not load recipient counts.')}</Alert.Content>
        </Alert.Root>
      )}

      {!isLoading && !error && !!preview && (
        <VStack align="stretch" gap={2}>
          <HStack justify="space-between">
            <Text fontSize="sm">Will receive</Text>
            <StatusChip variant="success">{preview.willReceive}</StatusChip>
          </HStack>
          <HStack justify="space-between">
            <Text fontSize="sm" color="fg.muted">No address</Text>
            <Text fontSize="sm">{preview.noAddress}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text fontSize="sm" color="fg.muted">Do not contact</Text>
            <Text fontSize="sm">{preview.doNotContact}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text fontSize="sm" color="fg.muted">Suppressed</Text>
            <Text fontSize="sm">{preview.suppressed}</Text>
          </HStack>
          <HStack justify="space-between" pt={2} borderTop="1px" borderColor="border">
            <Text fontSize="sm" fontWeight="semibold">Total</Text>
            <Text fontSize="sm" fontWeight="semibold">{preview.total}</Text>
          </HStack>
        </VStack>
      )}
    </Card>
  );
}
